/**
 * What the app has distilled about a vertical, as its render carries it:
 * `verticals/<name>.md`, written by `VerticalRenders` in the app. The header
 * line under the title ends in `updated <date>`; `## State` holds the prose,
 * then "Open threads:" and "Settled:" lists. Only curated verticals have one.
 */

export interface Distilled {
  state: string;
  open: string[];
  settled: string[];
  /** When it was rendered, as the header line says it. */
  date: string | null;
}

/** Where a vertical's render lives: its name made safe for a filename the way
 *  the app makes it. */
export function renderPathFor(name: string): string | null {
  const safe = name
    // eslint-disable-next-line no-control-regex
    .replace(/[\\/:*?"<>|\u0000-\u001F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\.+$/, '')
    .slice(0, 80);
  return safe ? `verticals/${safe}.md` : null;
}

/** The distilled state in a render, or null where it has none. */
export function parseDistilled(text: string): Distilled | null {
  const lines = text.split('\n');
  const at = lines.findIndex((line) => /^##\s+State\s*$/.test(line));
  if (at < 0) return null;

  const header = lines.find((line) => line.startsWith('`'));
  const date = header?.match(/updated\s+([^·]+?)\s*(?:·|$)/)?.[1] ?? null;

  const prose: string[] = [];
  const open: string[] = [];
  const settled: string[] = [];
  let into: string[] = prose;
  for (const line of lines.slice(at + 1)) {
    if (/^##\s/.test(line)) break;
    const t = line.trim();
    if (t === 'Open threads:') into = open;
    else if (t === 'Settled:') into = settled;
    else if (into === prose) {
      if (t) prose.push(t);
    } else if (t.startsWith('- ')) {
      into.push(t.slice(2).replace(/\*\*([^*]+)\*\*/g, '$1'));
    }
  }
  const state = prose.join(' ');
  return state ? { state, open, settled, date } : null;
}
